/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { timelogActionRequest } from "../../redux/action/timelogAction";
import Utils from "../../utility/utility";
import CustomModal from "../../components/CustomModal";
import DataTable from "../../components/table/DataTable";
import { ToneLegend, clockTime, dayState, dayTone } from "./timelogTone";

const DeveloperTimelog = (props) => {
  const [fromDate, setFromDate] = useState(Utils.getCurrentMonthDates().start);
  const [toDate, setToDate] = useState(Utils.getCurrentMonthDates().end);
  // which month the toggle is showing, so the active one can be marked
  const [monthView, setMonthView] = useState("current");
  const [modalIsOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState("");

  const dispatch = useDispatch();

  const timelogList = useSelector((state) => state.timelogReducer);
  const signdata = useSelector((state) => state.signinReducers);
  const access_token = signdata?.data?.access_token;

  useEffect(() => {
    const params = {
      start_time: fromDate ? Utils.fromDateTimeStamp(fromDate) : "",
      end_time: toDate ? Utils.toDateTimeStamp(toDate) : "",
      /* The table below is client-side: search, the State facet, sorting and
         the pager all run over whatever this call returns, so it has to
         return the WHOLE range. A month is 31 rows at most; asking for 10
         was cutting the listing off after the 10th of the month and leaving
         a pager that could not reach the rest. */
      pageSize: 100,
      orderby: "",
      order: "",
      page: 1,
    };

    if (access_token && signdata?.data?.role) {
      dispatch(timelogActionRequest(params, access_token, signdata?.data?.role));
    } else {
      console.error("Timelog List error");
    }
  }, [access_token, fromDate, toDate]);

  function closeModal() {
    setIsOpen(false);
  }

  const changeTimestampforPreviousMonth = () => {
    const previous = Utils.getPreviousMonthDates();
    setFromDate(previous?.start);
    setToDate(previous?.end);
  };

  const changeTimestampForCurrentMonth = () => {
    const current = Utils.getCurrentMonthDates();
    setFromDate(current?.start);
    setToDate(current?.end);
  };

  const rows = timelogList?.data?.timelog ?? [];

  const columns = useMemo(
    () => [
      {
        id: "serial",
        header: "#",
        enableSorting: false,
        enableHiding: false,
        meta: { width: "56px" },
        /* `row.index` is the position in the unsorted source array, so it
           renumbers nothing when the table is sorted or filtered and repeats
           itself across pages. `getDisplayIndex()` is the row's place in what
           the user is actually looking at. */
        cell: ({ row }) => (
          <span className="text-nowrap">{row.getDisplayIndex() + 1}</span>
        ),
      },
      {
        id: "formated_date",
        accessorFn: (row) => row?.formated_date ?? "",
        header: "Date",
        enableHiding: false,
        cell: ({ row }) => (
          <span className="text-nowrap fw-medium">
            {row.original?.formated_date ?? "NA"}
          </span>
        ),
      },
      {
        id: "start_time",
        accessorFn: (row) => Number(row?.start_time) || 0,
        header: "Checkin",
        cell: ({ row }) => (
          <span className="text-nowrap">{clockTime(row.original?.start_time)}</span>
        ),
      },
      {
        id: "end_time",
        accessorFn: (row) => Number(row?.end_time) || 0,
        header: "Checkout",
        cell: ({ row }) => (
          <span className="text-nowrap">{clockTime(row.original?.end_time)}</span>
        ),
      },
      {
        accessorKey: "break",
        header: "Break",
        cell: ({ row }) => (
          <span className="text-nowrap">{row.original?.break || "NA"}</span>
        ),
      },
      {
        accessorKey: "total_time",
        header: "Total Hour",
        cell: ({ row }) => (
          <span className="text-nowrap">{row.original?.total_time ?? "NA"}</span>
        ),
      },
      {
        accessorKey: "productive_time",
        header: "Productive Hour",
        cell: ({ row }) => (
          <span className="text-nowrap">
            {row.original?.productive_time ?? "NA"}
          </span>
        ),
      },
      {
        /* The colour used to be the ONLY carrier of this - unreadable to a
           screen reader and to anyone who cannot separate the red row from
           the green one. It is a column now; the wash just makes it scannable. */
        id: "state",
        accessorFn: (row) => dayState(row),
        header: "State",
        cell: ({ row }) => (
          <span className="text-nowrap">{dayState(row.original)}</span>
        ),
      },
      {
        id: "comments",
        header: "Comments",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) =>
          row.original?.no_break_reason ? (
            <button
              type="button"
              className="dt-btn"
              onClick={() => {
                setModalData(row.original?.no_break_reason);
                setIsOpen(true);
              }}
            >
              <i className="fa fa-eye" aria-hidden="true"></i>
              Reason
            </button>
          ) : null,
      },
    ],
    []
  );

  return (
    <>
      <div className={props?.data?.transtion == true ? "contents" : "content"}>
        <div className="content-top-spacer"></div>
        <div className="color-body">
          <div className="page-head">
            <div className="page-eyebrow">Tracking</div>
            <h1 className="page-title">Timelog</h1>
          </div>
          <div className="col-md-12">
            <div className="card-input mt-3">
              <div className="col-md-12 mb-3">
                <div className="mt-3">
                  <div
                    className="month-toggle"
                    role="group"
                    aria-label="Choose month"
                  >
                    <button
                      type="button"
                      className={
                        "month-btn" +
                        (monthView === "current" ? " is-active" : "")
                      }
                      aria-pressed={monthView === "current"}
                      onClick={() => {
                        setMonthView("current");
                        changeTimestampForCurrentMonth();
                      }}
                    >
                      {Utils.momentDate(new Date(), "MMMM")}
                    </button>
                    <button
                      type="button"
                      className={
                        "month-btn" +
                        (monthView === "previous" ? " is-active" : "")
                      }
                      aria-pressed={monthView === "previous"}
                      onClick={() => {
                        setMonthView("previous");
                        changeTimestampforPreviousMonth();
                      }}
                    >
                      {Utils.momentSubtract(1)}
                    </button>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="col-md-12"></div>
                  <div className="">
                    <div className="mt-3">
                      <DataTable
                        columns={columns}
                        data={rows}
                        isLoading={timelogList?.isLoading}
                        getRowId={(row, index) => String(row?.id ?? index)}
                        filters={<ToneLegend />}
                        search={{
                          columns: ["formated_date"],
                          placeholder: "Search days...",
                        }}
                        facets={[{ columnId: "state", label: "State" }]}
                        rowTone={(row) => dayTone(row.original)}
                        pagination={{
                          pageSize: 50,
                          pageSizeOptions: [25, 50, 100],
                        }}
                        emptyText="No record found"
                      />

                      <CustomModal
                        isShow={modalIsOpen}
                        onHide={closeModal}
                        heading="No break reason"
                        footer={
                          <button
                            type="button"
                            className="app-dialog-btn app-dialog-btn-ghost"
                            onClick={closeModal}
                          >
                            Close
                          </button>
                        }
                      >
                        <p className="app-dialog-lede">
                          {modalData || "No reason was given."}
                        </p>
                      </CustomModal>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeveloperTimelog;
